import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Button from '../common/Button';
import AddressEditor from './AddressEditor';
import NotesEditor from './NotesEditor';
import ContactsPanel from './ContactsPanel';
import OrderDetail from '../orders/OrderDetail';
import { useAuth } from '../../context/AuthContext';
import { impersonateUser } from '../../services/auth';
import { formatDate, formatPhone, formatCurrency } from '../../utils/formatters';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, VOLUME_LABELS, FLOUR_TYPE_LABELS } from '../../utils/constants';
import './CustomerForm.css';

const DEFAULT_PAYMENT_TERMS = 15;

const TABS = [
  { id: 'info',      label: 'Info' },
  { id: 'addresses', label: 'Addresses' },
  { id: 'contacts',  label: 'Contacts' },
  { id: 'orders',    label: 'Orders' },
  { id: 'notes',     label: 'Notes' },
];

/**
 * Customer (company) detail form — tabbed layout with Info, Addresses,
 * Contacts, Orders, and Notes tabs.
 */
const CustomerForm = ({
  customer,
  orders,
  users,
  archivedUsers,
  archivedUsersLoaded,
  onCancel,
  onUpdateCustomer,
  onAddAddress,
  onEditAddress,
  onDeleteAddress,
  onSetDefaultAddress,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onOrderStatusUpdate,
  onOrderNotesUpdate,
  onArchiveUser,
  onUnarchiveUser,
  onLoadArchivedUsers,
  isSaving,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('info');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSaving, setOrderSaving] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [impersonateError, setImpersonateError] = useState(null);

  // ── Inline edit state ──────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editError, setEditError] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const firstInputRef = useRef(null);

  const handleEditStart = useCallback(() => {
    setEditData({
      company_name: customer?.company_name || '',
      phone: customer?.phone || '',
      delivery_region: customer?.delivery_region || '',
      payment_terms: customer?.payment_terms ?? DEFAULT_PAYMENT_TERMS,
      tax_exempt: customer?.tax_exempt ?? false,
    });
    setEditError(null);
    setIsEditing(true);
  }, [customer]);

  // Focus the first input when entering edit mode
  useEffect(() => {
    if (isEditing) {
      firstInputRef.current?.focus();
    }
  }, [isEditing]);

  const handleEditCancel = useCallback(() => {
    setIsEditing(false);
    setEditData({});
    setEditError(null);
  }, []);

  const handleEditChange = useCallback((field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!onUpdateCustomer || !customer) return;
    // Only send fields that changed
    const changes = {};
    if (editData.company_name !== (customer.company_name || '')) changes.company_name = editData.company_name;
    if (editData.phone !== (customer.phone || '')) changes.phone = editData.phone;
    if (editData.delivery_region !== (customer.delivery_region || '')) changes.delivery_region = editData.delivery_region;
    if (Number(editData.payment_terms) !== (customer.payment_terms ?? DEFAULT_PAYMENT_TERMS)) changes.payment_terms = Number(editData.payment_terms);
    if (editData.tax_exempt !== (customer.tax_exempt ?? false)) changes.tax_exempt = editData.tax_exempt;

    if (Object.keys(changes).length === 0) {
      setIsEditing(false);
      return;
    }

    if (changes.company_name !== undefined && !changes.company_name.trim()) {
      setEditError('Company name is required.');
      return;
    }

    try {
      setEditSaving(true);
      setEditError(null);
      await onUpdateCustomer(customer.id, changes);
      setIsEditing(false);
    } catch (err) {
      setEditError(err?.detail || err?.message || 'Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  }, [onUpdateCustomer, customer, editData]);

  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  }, [handleEditSave, handleEditCancel]);

  const handleNewOrder = useCallback(() => {
    navigate('/employee/customers/new-order', {
      state: { customerId: customer?.id, companyName: customer?.company_name },
    });
  }, [navigate, customer]);

  const handleOrderClick = useCallback((order) => {
    setSelectedOrder(order);
  }, []);

  const handleOrderBack = useCallback(() => {
    setSelectedOrder(null);
  }, []);

  const handleStatusUpdate = useCallback(async (newStatus) => {
    if (!selectedOrder || !onOrderStatusUpdate) return;
    const prevStatus = selectedOrder.status;
    setOrderError(null);
    // Optimistically update so the select reflects the change immediately
    setSelectedOrder((prev) => ({ ...prev, status: newStatus }));
    try {
      setOrderSaving(true);
      const updated = await onOrderStatusUpdate(selectedOrder.id, newStatus);
      // Confirm with server response
      if (updated) setSelectedOrder(updated);
    } catch (err) {
      // Revert to previous status on failure
      setSelectedOrder((prev) => ({ ...prev, status: prevStatus }));
      setOrderError(err?.message || 'Failed to update status.');
    } finally {
      setOrderSaving(false);
    }
  }, [selectedOrder, onOrderStatusUpdate]);

  const handleNotesUpdate = useCallback(async (newNotes) => {
    if (!selectedOrder || !onOrderNotesUpdate) return;
    // Optimistically update textarea value
    setSelectedOrder((prev) => ({ ...prev, internal_notes: newNotes }));
  }, [selectedOrder, onOrderNotesUpdate]);

  const handleNotesSave = useCallback(async () => {
    if (!selectedOrder || !onOrderNotesUpdate) return;
    setOrderError(null);
    try {
      setOrderSaving(true);
      await onOrderNotesUpdate(selectedOrder.id, selectedOrder.internal_notes || '');
    } catch (err) {
      setOrderError(err?.message || 'Failed to save notes.');
    } finally {
      setOrderSaving(false);
    }
  }, [selectedOrder, onOrderNotesUpdate]);

  if (!customer) return null;

  return (
    <div className="customer-form">

      {/* ── Tab bar ──────────────────────────────────────────── */}
      <div className="customer-form__tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            type="button"
            className={`customer-form__tab${activeTab === tab.id ? ' customer-form__tab--active' : ''}`}
            aria-selected={activeTab === tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSelectedOrder(null);
            }}
          >
            {tab.label}
            {tab.id === 'orders' && orders?.length > 0 && (
              <span className="customer-form__tab-badge">{orders.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab panels ───────────────────────────────────────── */}

      {/* INFO ───────────────────────────────────────────────── */}
      {activeTab === 'info' && (
        <div className="customer-form__panel" role="tabpanel">
          {/* Edit toolbar */}
          {onUpdateCustomer && (
            <div className="customer-form__info-toolbar">
              {isEditing ? (
                <>
                  {editError && (
                    <span className="customer-form__edit-error">{editError}</span>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleEditCancel}
                    disabled={editSaving}
                    className="customer-form__edit-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleEditSave}
                    disabled={editSaving}
                    className="customer-form__edit-btn"
                  >
                    {editSaving ? 'Saving…' : 'Save'}
                  </Button>
                </>
              ) : (
                <button
                  type="button"
                  className="customer-form__edit-toggle"
                  onClick={handleEditStart}
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11.5 1.5l3 3-9 9H2.5v-3l9-9z" />
                  </svg>
                  Edit
                </button>
              )}
            </div>
          )}

          <div className="customer-form__info-grid">
            {/* ── Editable fields ──────────────────────────── */}
            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Company Name</span>
              {isEditing ? (
                <input
                  ref={firstInputRef}
                  type="text"
                  className="customer-form__info-input"
                  value={editData.company_name}
                  onChange={(e) => handleEditChange('company_name', e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  disabled={editSaving}
                />
              ) : (
                <span className="customer-form__info-value">{customer.company_name}</span>
              )}
            </div>
            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Phone</span>
              {isEditing ? (
                <input
                  type="tel"
                  className="customer-form__info-input"
                  value={editData.phone}
                  onChange={(e) => handleEditChange('phone', e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  placeholder="e.g. 540-555-1234"
                  disabled={editSaving}
                />
              ) : (
                <span className="customer-form__info-value">
                  {customer.phone ? formatPhone(customer.phone) : <em className="customer-form__empty">—</em>}
                </span>
              )}
            </div>
            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Delivery Region</span>
              {isEditing ? (
                <select
                  className="customer-form__info-input"
                  value={editData.delivery_region}
                  onChange={(e) => handleEditChange('delivery_region', e.target.value)}
                  disabled={editSaving}
                >
                  <option value="">— none —</option>
                  <option value="local">local</option>
                  <option value="cville">cville</option>
                  <option value="east">east</option>
                  <option value="north">north</option>
                  <option value="west">west</option>
                  <option value="south">south</option>
                  <option value="far">far</option>
                  <option value="valley">valley</option>
                </select>
              ) : (
                <span className="customer-form__info-value">
                  {customer.delivery_region || <em className="customer-form__empty">—</em>}
                </span>
              )}
            </div>

            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Payment Terms</span>
              {isEditing ? (
                <select
                  className="customer-form__info-input"
                  value={editData.payment_terms}
                  onChange={(e) => handleEditChange('payment_terms', e.target.value)}
                  disabled={editSaving}
                >
                  <option value={15}>Net 15</option>
                  <option value={30}>Net 30</option>
                  <option value={60}>Net 60</option>
                </select>
              ) : (
                <span className="customer-form__info-value">Net {customer.payment_terms ?? DEFAULT_PAYMENT_TERMS}</span>
              )}
            </div>

            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Tax Exempt</span>
              {isEditing ? (
                <label className="customer-form__checkbox-label">
                  <input
                    type="checkbox"
                    className="customer-form__checkbox"
                    checked={editData.tax_exempt}
                    onChange={(e) => handleEditChange('tax_exempt', e.target.checked)}
                    disabled={editSaving}
                  />
                  <span>Tax exempt</span>
                </label>
              ) : (
                <span className="customer-form__info-value">
                  {customer.tax_exempt ? 'Yes' : 'No'}
                </span>
              )}
            </div>

            {/* ── Read-only fields ─────────────────────────── */}
            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Orders</span>
              <span className="customer-form__info-value">{customer.order_count ?? 0}</span>
            </div>
            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Contacts</span>
              <span className="customer-form__info-value">{customer.user_count ?? 0}</span>
            </div>
            <div className="customer-form__info-item">
              <span className="customer-form__info-label">Customer Since</span>
              <span className="customer-form__info-value">{formatDate(customer.created_at)}</span>
            </div>
          </div>

          {/* ── Application details (portal signups) ─────────── */}
          {customer.business_types?.length > 0 && (
            <div className="customer-form__application">
              <p className="customer-form__application-heading">Application Details</p>
              <div className="customer-form__info-grid">
                <div className="customer-form__info-item">
                  <span className="customer-form__info-label">Business Type</span>
                  <span className="customer-form__info-value">
                    {customer.business_types.map(t =>
                      t === 'Other' && customer.operation_type_other
                        ? `Other (${customer.operation_type_other})`
                        : t
                    ).join(', ')}
                  </span>
                </div>
                <div className="customer-form__info-item">
                  <span className="customer-form__info-label">Est. Volume</span>
                  <span className="customer-form__info-value">
                    {VOLUME_LABELS[customer.estimated_monthly_volume] || customer.estimated_monthly_volume || <em className="customer-form__empty">—</em>}
                  </span>
                </div>
                {customer.has_loading_dock !== null && customer.has_loading_dock !== undefined && (
                  <div className="customer-form__info-item">
                    <span className="customer-form__info-label">Loading Dock</span>
                    <span className="customer-form__info-value">{customer.has_loading_dock ? 'Yes' : 'No'}</span>
                  </div>
                )}
                {customer.current_supplier && (
                  <div className="customer-form__info-item">
                    <span className="customer-form__info-label">Current Supplier</span>
                    <span className="customer-form__info-value">{customer.current_supplier}</span>
                  </div>
                )}
                {customer.application_flour_types?.length > 0 && (
                  <div className="customer-form__info-item customer-form__info-item--full">
                    <span className="customer-form__info-label">Flour Types</span>
                    <span className="customer-form__info-value">
                      {customer.application_flour_types.map(f => FLOUR_TYPE_LABELS[f] || f).join(', ')}
                    </span>
                  </div>
                )}
                {customer.how_did_you_hear && (
                  <div className="customer-form__info-item customer-form__info-item--full">
                    <span className="customer-form__info-label">How They Heard</span>
                    <span className="customer-form__info-value">{customer.how_did_you_hear}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ADDRESSES ──────────────────────────────────────────── */}
      {activeTab === 'addresses' && (
        <div className="customer-form__panel" role="tabpanel">
          <AddressEditor
            companyId={customer.id}
            addresses={customer.addresses || []}
            onAdd={onAddAddress}
            onEdit={onEditAddress}
            onDelete={onDeleteAddress}
            onSetDefault={onSetDefaultAddress}
            isSaving={isSaving}
          />
        </div>
      )}

      {/* CONTACTS ───────────────────────────────────────────── */}
      {activeTab === 'contacts' && (
        <div className="customer-form__panel" role="tabpanel">
          <ContactsPanel
            users={users || []}
            archivedUsers={archivedUsers || []}
            archivedLoaded={archivedUsersLoaded}
            onArchive={onArchiveUser}
            onUnarchive={onUnarchiveUser}
            onLoadArchived={onLoadArchivedUsers}
            isSaving={isSaving}
          />
        </div>
      )}

      {/* ORDERS ─────────────────────────────────────────────── */}
      {activeTab === 'orders' && (
        <div className="customer-form__panel customer-form__panel--orders" role="tabpanel">
          {selectedOrder ? (
            /* ── Order detail inline ──────────────────────── */
            <div className="customer-form__order-detail-wrap">
              <div className="customer-form__order-detail-back">
                <button
                  type="button"
                  className="customer-form__order-back-btn"
                  onClick={handleOrderBack}
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 4L6 8L10 12" />
                  </svg>
                  Back to orders
                </button>
                {orderSaving && (
                  <span className="customer-form__order-saving">Saving…</span>
                )}
                {orderError && (
                  <span className="customer-form__order-error">{orderError}</span>
                )}
              </div>
              <OrderDetail
                order={selectedOrder}
                isEmployee
                onStatusUpdate={onOrderStatusUpdate ? handleStatusUpdate : undefined}
                onNotesUpdate={onOrderNotesUpdate ? handleNotesUpdate : undefined}
              />
              {onOrderNotesUpdate && (
                <div className="customer-form__order-notes-save">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleNotesSave}
                    disabled={orderSaving}
                  >
                    Save Notes
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* ── Order list ───────────────────────────────── */
            <>
              <div className="customer-form__orders-toolbar">
                <Button type="button" variant="primary" onClick={handleNewOrder}>
                  + New Order
                </Button>
              </div>
              {!orders || orders.length === 0 ? (
                <p className="customer-form__empty-section">No orders found.</p>
              ) : (
                <div className="customer-form__orders">
                  <table className="customer-form__orders-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Status</th>
                        <th>Total</th>
                        <th>Delivery Date</th>
                        <th>Delivery Address</th>
                        <th>Created</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr
                          key={order.id}
                          className="customer-form__orders-row"
                          onClick={() => handleOrderClick(order)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleOrderClick(order)}
                          aria-label={`View order ${order.order_number}`}
                        >
                          <td className="customer-form__order-num">{order.order_number}</td>
                          <td>
                            <span
                              className="customer-form__order-status"
                              style={{ '--status-color': ORDER_STATUS_COLORS[order.status] || '#888' }}
                            >
                              {ORDER_STATUS_LABELS[order.status] || order.status}
                            </span>
                          </td>
                          <td>{formatCurrency((order.total_amount_cents || 0) / 100)}</td>
                          <td>{order.delivery_date ? formatDate(order.delivery_date) : '—'}</td>
                          <td className="customer-form__order-address">
                            {order.delivery_address_display
                              ? `${order.delivery_address_display.street_address}, ${order.delivery_address_display.city}, ${order.delivery_address_display.state}`
                              : <em className="customer-form__empty">—</em>}
                          </td>
                          <td>{formatDate(order.created_at)}</td>
                          <td className="customer-form__order-chevron-cell">
                            <svg className="customer-form__order-chevron" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M6 4L10 8L6 12" />
                            </svg>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* NOTES ──────────────────────────────────────────────── */}
      {activeTab === 'notes' && (
        <div className="customer-form__panel" role="tabpanel">
          <NotesEditor
            companyId={customer.id}
            notes={customer.notes || []}
            onAdd={onAddNote}
            onEdit={onEditNote}
            onDelete={onDeleteNote}
            isSaving={isSaving}
          />
        </div>
      )}

      {/* ── Footer actions ────────────────────────────────────── */}
      {impersonateError && (
        <div className="customer-form__error" role="alert">{impersonateError}</div>
      )}
      <div className="customer-form__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSaving}
        >
          Back to List
        </Button>
        {user?.is_superuser && users?.length > 0 && (
          <Button
            type="button"
            variant="secondary"
            onClick={async () => {
              try {
                setImpersonateError(null);
                await impersonateUser(users[0].user.id);
              } catch (err) {
                setImpersonateError(err?.message || 'Failed to impersonate user.');
              }
            }}
          >
            Login as Customer
          </Button>
        )}
      </div>
    </div>
  );
};


CustomerForm.propTypes = {
  customer: PropTypes.object,
  orders: PropTypes.array,
  users: PropTypes.array,
  archivedUsers: PropTypes.array,
  archivedUsersLoaded: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onUpdateCustomer: PropTypes.func,
  onAddAddress: PropTypes.func.isRequired,
  onEditAddress: PropTypes.func.isRequired,
  onDeleteAddress: PropTypes.func.isRequired,
  onSetDefaultAddress: PropTypes.func.isRequired,
  onAddNote: PropTypes.func.isRequired,
  onEditNote: PropTypes.func.isRequired,
  onDeleteNote: PropTypes.func.isRequired,
  onOrderStatusUpdate: PropTypes.func,
  onOrderNotesUpdate: PropTypes.func,
  onArchiveUser: PropTypes.func.isRequired,
  onUnarchiveUser: PropTypes.func.isRequired,
  onLoadArchivedUsers: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default CustomerForm;
